import { createClient } from '@/lib/supabase/client';
import { callEmailFunction } from '@/lib/email';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface NotificationPayload {
  recipientId: string;
  senderId?: string;
  type: NotificationType;
  title: string;
  body: string;
  entityType?: string;
  entityId?: string;
}

export type NotificationType =
  | 'mct_buy' |'mct_sell' |'campaign_support' |'wallet_funded' |'like' |'comment' |'reply' |'mention' |'repost' |'track_like' |'track_comment' |'track_share' |'follow' |'value_change' |'momentum_gain' |'general';

// ─── Core: Create in-app notification ────────────────────────────────────────
export async function createNotification(payload: NotificationPayload): Promise<string | null> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('notifications')
      .insert({
        recipient_id: payload.recipientId,
        sender_id: payload.senderId || null,
        type: payload.type,
        title: payload.title,
        body: payload.body,
        entity_type: payload.entityType || 'general',
        entity_id: payload.entityId || null,
        is_read: false,
      })
      .select('id')
      .single();

    if (error) {
      console.error('createNotification error:', error.message);
      return null;
    }
    return data?.id || null;
  } catch (err) {
    console.error('createNotification exception:', err);
    return null;
  }
}

// ─── Core: Send email notification ───────────────────────────────────────────
export async function sendEmailNotification(
  to: string,
  name: string,
  title: string,
  body: string
): Promise<void> {
  try {
    await callEmailFunction({ type: 'notification', to, name, notifTitle: title, notifBody: body });
  } catch {
    // Email failure must never break the UI
  }
}

// ─── Core: Mark notification as read ─────────────────────────────────────────
export async function markAsRead(notificationId: string): Promise<void> {
  try {
    const supabase = createClient();
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);
  } catch {}
}

export async function markAllAsRead(userId: string): Promise<void> {
  try {
    const supabase = createClient();
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('recipient_id', userId)
      .eq('is_read', false);
  } catch {}
}

// ─── MCT Buy Notification ─────────────────────────────────────────────────────
export async function notifyMCTBuy(params: {
  buyerUserId: string;
  buyerEmail: string;
  buyerName: string;
  artistId: string;
  artistEmail?: string;
  artistName?: string;
  campaignId: string;
  campaignTitle: string;
  quantity: number;
  totalPaid: number;
  walletBalance?: number;
}): Promise<void> {
  const {
    buyerUserId, buyerEmail, buyerName,
    artistId, artistEmail, artistName,
    campaignId, campaignTitle, quantity, totalPaid,
  } = params;

  // In-app: buyer
  await createNotification({
    recipientId: buyerUserId,
    senderId: buyerUserId,
    type: 'mct_buy',
    title: 'MCT Purchase Confirmed',
    body: `You bought ${quantity.toLocaleString()} MCTs for "${campaignTitle}". Total paid: ₦${totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    entityType: 'campaign',
    entityId: campaignId,
  });

  // Email: buyer
  await sendEmailNotification(
    buyerEmail,
    buyerName,
    'MCT Purchase Confirmed',
    `You bought ${quantity.toLocaleString()} MCTs for "${campaignTitle}". Total paid: ₦${totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}.`
  );

  // In-app: artist
  if (artistId && artistId !== buyerUserId) {
    await createNotification({
      recipientId: artistId,
      senderId: buyerUserId,
      type: 'campaign_support',
      title: 'New MCT Supporter!',
      body: `${buyerName} bought ${quantity.toLocaleString()} MCTs for your campaign "${campaignTitle}"`,
      entityType: 'campaign',
      entityId: campaignId,
    });

    // Email: artist
    if (artistEmail) {
      await sendEmailNotification(
        artistEmail,
        artistName || 'Artist',
        'New MCT Supporter!',
        `${buyerName} bought ${quantity.toLocaleString()} MCTs for your campaign "${campaignTitle}".`
      );
    }
  }
}

// ─── MCT Sell Notification ────────────────────────────────────────────────────
export async function notifyMCTSell(params: {
  sellerUserId: string;
  sellerEmail: string;
  sellerName: string;
  artistId?: string;
  artistEmail?: string;
  artistName?: string;
  campaignId: string;
  campaignTitle: string;
  quantity: number;
  netCredit: number;
}): Promise<void> {
  const {
    sellerUserId, sellerEmail, sellerName,
    artistId, artistEmail, artistName,
    campaignId, campaignTitle, quantity, netCredit,
  } = params;

  // In-app: seller
  await createNotification({
    recipientId: sellerUserId,
    senderId: sellerUserId,
    type: 'mct_sell',
    title: 'MCT Sell Confirmed',
    body: `You sold ${quantity.toLocaleString()} MCTs from "${campaignTitle}". Wallet credited: ₦${netCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    entityType: 'campaign',
    entityId: campaignId,
  });

  // Email: seller
  await sendEmailNotification(
    sellerEmail,
    sellerName,
    'MCT Sell Confirmed',
    `You sold ${quantity.toLocaleString()} MCTs from "${campaignTitle}". Your wallet has been credited ₦${netCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })} (after 10% service charge).`
  );

  // In-app: artist
  if (artistId && artistId !== sellerUserId) {
    await createNotification({
      recipientId: artistId,
      senderId: sellerUserId,
      type: 'value_change',
      title: 'MCT Sell Activity',
      body: `${sellerName} sold ${quantity.toLocaleString()} MCTs from your campaign "${campaignTitle}"`,
      entityType: 'campaign',
      entityId: campaignId,
    });
  }
}

// ─── Wallet Funded Notification ───────────────────────────────────────────────
export async function notifyWalletFunded(params: {
  userId: string;
  userEmail: string;
  userName: string;
  amount: number;
  newBalance: number;
}): Promise<void> {
  const { userId, userEmail, userName, amount, newBalance } = params;

  await createNotification({
    recipientId: userId,
    senderId: userId,
    type: 'wallet_funded',
    title: 'Wallet Funded Successfully',
    body: `₦${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} added to your EchoMint wallet. New balance: ₦${newBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
    entityType: 'wallet',
  });

  await sendEmailNotification(
    userEmail,
    userName,
    'Wallet Funded Successfully',
    `₦${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} has been added to your EchoMint wallet. Your new balance is ₦${newBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}.`
  );
}

// ─── Post Like Notification ───────────────────────────────────────────────────
export async function notifyPostLike(params: {
  postOwnerId: string;
  likerId: string;
  likerName: string;
  postId: string;
  postPreview: string;
}): Promise<void> {
  const { postOwnerId, likerId, likerName, postId, postPreview } = params;
  if (postOwnerId === likerId) return;

  await createNotification({
    recipientId: postOwnerId,
    senderId: likerId,
    type: 'like',
    title: 'Someone liked your post',
    body: `${likerName} liked your post: "${postPreview.slice(0, 60)}..."`,
    entityType: 'post',
    entityId: postId,
  });
}

// ─── Post Comment Notification ────────────────────────────────────────────────
export async function notifyPostComment(params: {
  postOwnerId: string;
  commenterId: string;
  commenterName: string;
  postId: string;
  commentContent: string;
  parentCommentOwnerId?: string;
}): Promise<void> {
  const { postOwnerId, commenterId, commenterName, postId, commentContent, parentCommentOwnerId } = params;

  if (postOwnerId !== commenterId) {
    await createNotification({
      recipientId: postOwnerId,
      senderId: commenterId,
      type: 'comment',
      title: 'New comment on your post',
      body: `${commenterName} commented: "${commentContent.slice(0, 80)}"`,
      entityType: 'post',
      entityId: postId,
    });
  }

  if (parentCommentOwnerId && parentCommentOwnerId !== commenterId && parentCommentOwnerId !== postOwnerId) {
    await createNotification({
      recipientId: parentCommentOwnerId,
      senderId: commenterId,
      type: 'reply',
      title: 'Someone replied to your comment',
      body: `${commenterName} replied: "${commentContent.slice(0, 80)}"`,
      entityType: 'post',
      entityId: postId,
    });
  }
}
